import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { IOrderMetaPlugin, OrderModel } from "../../../../../../models/rightstore/rightstore.models";
import { ReportsService } from "../../../../../../services/reports/reports.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { EntityFinderSearch } from "../../../../../components/entity-finder/entity-finder-search";
import { CompanyService } from "../../../../../../services/modules/company-client.service";
import { CompanyModel } from "../../../../../../models/companies/company.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
@transient()
@useView('views/pages/modules/orders/plugins/company-report/company-report.html')
export class CompanyReport implements IOrderMetaPlugin {

    private companyModel: CompanyModel;

    public companyId: number;
    public companyName: string;
    public reportWasAlreadyPurchased: boolean;
    public validator: ClientSideValidator;
    public companyEntityTypes: EntityTypeConfigurationModel[];
    public canSubmit: boolean = true;
    public order: OrderModel;
    public viewTitle: string;
    public docVM: EntityFinderSearch;
    public isCompanyReportActiveSubscription: boolean;

    @computedFrom('reportWasAlreadyPurchased', 'isCompanyReportActiveSubscription')
    public get disableSubmit(): boolean {
        return this.reportWasAlreadyPurchased || this.isCompanyReportActiveSubscription;
    }

    @computedFrom('isCompanyReportActiveSubscription')
    public get disableDoLater(): boolean {
        return this.isCompanyReportActiveSubscription;
    }

    constructor(
        public validationsClientService: ValidationsClientService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private reportsService: ReportsService,
        private entityValidationsService: EntityValidationsService,
        private configService: ConfigService,
        private companyService: CompanyService) {
        this.companyEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["CompanySearch"]);
    }

    async activate(model: { order: OrderModel, companyId:number }) {
        this.order = model.order;
        this.viewTitle = this.order.product.name;
        const reportId = this.order.product.variantAttributes.filter(x => x.dimension === 'ReportId')[0].value;
        this.companyId = model.companyId;
        if (this.companyId) {
            const companyResponse = await this.companyService.getCompany(this.companyId);
            this.companyModel = companyResponse.company;
        } else {
            this.companyId = null;
        }

        this.isCompanyReportActiveSubscription = await this.reportsService.isCompanyReportActiveSubscription(Number(reportId));
    }

    attached() {
        if (this.companyModel) {
            this.docVM.selectEntity(null, this.companyModel, 'CompanySearch', true, false, false);
        }
    }

    searchCompanySelected(e: any) {
        if (e.detail.selectedEntity) {
            this.companyId = e.detail.selectedEntity.id;
            this.companyName = e.detail.selectedEntity.fullName;
            this.reportsService.isCompanyReportPurchased(this.order.product.code, this.companyId, false).then(response => {
                this.reportWasAlreadyPurchased = response.anyFulfilledOrders;
            });
        } else {
            this.companyId = null;
            this.reportWasAlreadyPurchased = false;
        }
    }

    navigateToRefundRequest() {
        window.open(this.configService.config.urls.rightStoreRefundUrl, "_blank");
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Company Name is required",
            parameters: null, property: "companyId",
            validationType: ValidationTypeEnum.Required
        }];

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Company Report",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    public async isValid(): Promise<boolean> {
        this.setValidator();
        return await this.validator.validate().then(x => {
            return x.valid
        });
    }

    public getMetaModel() {
        const metaModel = {
            companyId: this.companyId
        };

        return metaModel;
    }
}